/**
 * Duration resolution utilities for time-off requested dates.
 *
 * A requested date can be a **Full Day** or **Partial Day** request. The API's
 * `durationFlag` is the authoritative source for this (`'F'` = Full Day,
 * `'P'` = Partial Day). Full-day records can still carry a start/end time
 * window (for example, a 4-hour full day), so inferring the duration purely
 * from the display `times` string misclassifies those records as Partial Day.
 * These helpers centralize the correct resolution so every edit/save flow
 * agrees on a single rule.
 */

/**
 * Minimal shape needed to resolve a requested date's Full/Partial duration.
 * Kept structural (rather than importing the slice's `RequestedDate`) so this
 * utility has no dependency on the store layer.
 */
export interface DurationResolvable {
  /** Authoritative API duration flag: `'F'` = Full Day, `'P'` = Partial Day. */
  durationFlag?: string
  /** Display time string, e.g. `'Full Day'` or `'8:00 AM - 12:00 PM'`. */
  times?: string
  /** Raw partial-day window start (ISO or formatted), when present. */
  startTime?: string | null
  /** Raw partial-day window end (ISO or formatted), when present. */
  endTime?: string | null
}

/**
 * Determines whether a requested date is a Full Day request.
 *
 * Resolution order:
 * 1. Trust the authoritative `durationFlag` when present (`'F'`/`'P'`).
 * 2. Otherwise (legacy data with no flag) infer Partial Day from the presence
 *    of a time window — either a non-"Full Day" `times` string or an explicit
 *    `startTime`/`endTime` pair.
 *
 * @param d - The requested date's duration-related fields.
 * @returns `true` for a Full Day request, `false` for Partial Day.
 */
export const isRequestedDateFullDay = (d: DurationResolvable): boolean => {
  if (d.durationFlag === 'F') return true
  if (d.durationFlag === 'P') return false
  const hasPartialWindow =
    (!!d.times && d.times !== 'Full Day') || (!!d.startTime && !!d.endTime)
  return !hasPartialWindow
}

/**
 * Resolves a requested date's duration label used by the create/edit forms.
 *
 * @param d - The requested date's duration-related fields.
 * @returns `'Full Day'` or `'Partial Day'`.
 */
export const resolveRequestedDateDuration = (
  d: DurationResolvable
): 'Full Day' | 'Partial Day' =>
  isRequestedDateFullDay(d) ? 'Full Day' : 'Partial Day'
